import React, { useContext } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Location } from '@reach/router'
import { makeStyles, useTheme } from '@material-ui/styles'
import { useTranslation } from 'react-i18next'
import classnames from 'classnames'

import { decodeText } from '../../utilities/helpers'
import Link from '../common/link'
import LocaleContext from '../context/localeContext'

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        marginTop: '20px',
        marginBottom: '20px',
        [theme.breakpoints.up('md')]: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            margin: 0
        }
    },
    link: {
        textTransform: 'initial',
        letterSpacing: 'initial',
        fontStretch: 'initial',
        fontWeight: 'initial',
        fontSize: '0.9em',
        color: theme.palette.text.primary,
        fontFamily: 'Segoe UI, SegoeUI, "Helvetica Neue", Helvetica, Arial, sans-serif',
        '&:hover, &:focus': { color: theme.palette.text.primary },
        [theme.breakpoints.up('md')]: { marginLeft: '1.33em' }
    },
    alignRight: {
        [theme.breakpoints.up('md')]: {
            justifyContent: 'flex-end'
        }
    },
    linksList: {
        fontSize: '0.72em',
        listStyleType: 'none',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        padding: 0,
        flex: 1,
        [theme.breakpoints.down('sm')]: {
            justifyContent: 'space-between'
        },
        [theme.breakpoints.up('md')]: {
            marginTop: '30px',
            marginBottom: '30px'
        }
    },
    locationPicker: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        color: theme.palette.text.primary,
        [theme.breakpoints.down('sm')]: {
            marginRight: `${theme.spacing(2)}px`
        }
    },
    locationPickerContainer: {
        maxWidth: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center'
    },
    locationPickerIcon: { paddingRight: '.5em' }
}))

const { getLocaleNameFromUrl, removeLocaleFromPath } = require('../../utilities/locale')

const FooterMicrosoft = () => {
    const _classes = useStyles()
    const { t } = useTranslation()
    const theme = useTheme()
    const localeContext = useContext(LocaleContext)

    return (
        <nav role='navigation' aria-label={t('Microsoft corporate links')} className={_classes.root}>
            <ul className={_classes.linksList}>
                <li className={_classes.locationPickerContainer}>
                    <Location>
                        {({ location }) =>
                            removeLocaleFromPath(location.pathname) !== '/locales' ? (
                                <Link
                                    classes={{ root: classnames(_classes.locationPicker, _classes.link) }}
                                    href='/locales'
                                    state={{ prevPath: removeLocaleFromPath(location.pathname) }}
                                    aria-label={t('slug:footer-language-selector-button', { lang: getLocaleNameFromUrl(location.pathname) })}
                                >
                                    <FontAwesomeIcon
                                        className={_classes.locationPickerIcon}
                                        icon={['tcr', 'world']}
                                        color={theme.palette.text.primary}
                                        size='2x'
                                        style={{ height: '1em' }}
                                        fixedWidth
                                    />
                                    {getLocaleNameFromUrl(location.pathname)}
                                </Link>
                            ) : (
                                <span className={_classes.locationPicker}>
                                    <FontAwesomeIcon
                                        className={_classes.locationPickerIcon}
                                        icon={['tcr', 'world']}
                                        color={theme.palette.text.primary}
                                        title={t('slug:footer-language-selector-button', { lang: getLocaleNameFromUrl(location.pathname) })}
                                        size='2x'
                                        style={{ height: '1em' }}
                                        fixedWidth
                                    />
                                    {getLocaleNameFromUrl(location.pathname)}
                                </span>
                            )
                        }
                    </Location>
                    </li>
                    <li>
                    <Link
                        classes={{ root: _classes.link }}
                        href='https://go.microsoft.com/fwlink/?linkid=2259814'
                        target='_blank'
                        rel='noopener noreferrer'
                    >
                        {t('Consumer Health Privacy')}
                    </Link>
                </li>
            </ul>
            <ul className={classnames(_classes.linksList, _classes.alignRight)}>
                <li>
                    <Link
                        classes={{ root: _classes.link }}
                        href='https://go.microsoft.com/fwlink/?LinkId=521839'
                        target='_blank'
                        rel='noopener noreferrer'
                    >
                        {decodeText(t('slug:privacy-cookies'))}
                    </Link>
                </li>
                <li>
                    <Link classes={{ root: _classes.link }} href='https://www.microsoft.com/trademarks' target='_blank' rel='noopener noreferrer'>
                        {t('Trademarks')}
                    </Link>
                </li>
                <li>
                    <Link
                        classes={{ root: _classes.link }}
                        href='https://www.microsoft.com/servicesagreement'
                        target='_blank'
                        rel='noopener noreferrer'
                    >
                        {t('Terms of Use')}
                    </Link>
                </li>
                {localeContext.locale === 'fr-fr' && (
                    <li>
                        <Link
                            classes={{ root: _classes.link }}
                            href='https://go.microsoft.com/fwlink/?linkid=2121428'
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            Accessibilité
                        </Link>
                    </li>
                )}
                {localeContext.locale === 'it-it' && (
                    <li>
                        <Link
                            classes={{ root: _classes.link }}
                            href='https://go.microsoft.com/fwlink/?linkid=2121429'
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            Accessibilità
                        </Link>
                    </li>
                )}
                <li className={_classes.link}>© Microsoft {new Date().getFullYear()}</li>
            </ul>
        </nav>
    )
}

export default FooterMicrosoft
